import {
	AC_CARD_EDITABLE_STATUSES,
	type AcCardStatus,
	type AcCardTransition,
	type AcCardTransitionAction,
	type AcCardTransitionActor,
	type MeResponse,
	type PermissionCode,
	type RoleCode,
	allowedTransitions,
	roleHasGmsAccess,
} from "@seagames/contracts";
import { formatYmd } from "../../lib/format/datetime";

/**
 * Client-side heuristics for the records pages. NONE of this decides
 * anything: every write is re-checked by the API (status graph, scope and
 * permission in SQL). It only chooses which buttons to show and which to
 * draw locked, so the list need not ask `/actions` per row.
 */

export const isAdminRole = (role: RoleCode | null | undefined): boolean => role === "SUPER_ADMIN" || role === "ADMIN";

/** The group's permission applies only while the active role is the group's, and the group is active. */
function groupGrants(me: MeResponse, permission: PermissionCode): boolean {
	return roleHasGmsAccess(me.active_role, me.group) && !!me.group?.permissions.includes(permission);
}

/** The sides of the status graph this session can act on (PLAN §3). */
export function actorSides(me: MeResponse): Set<AcCardTransitionActor> {
	const role = me.active_role;
	if (role === "SUPER_ADMIN") return new Set(["ORG", "HOST", "SUPER_ADMIN"]);
	if (role === "ADMIN") return new Set(["ORG", "HOST"]);
	const sides = new Set<AcCardTransitionActor>();
	if (groupGrants(me, "accreditation.submit")) sides.add("ORG");
	// A NOC-type (ORGANIZATION) group can never act as host (K3).
	if (role === "OPERATION" && groupGrants(me, "accreditation.approve")) sides.add("HOST");
	return sides;
}

/**
 * Host rights: ADMIN / SUPER_ADMIN, or a group holding `accreditation.approve`.
 * Lead decision B8: a host GROUP never "unsubmits" (SUBMITTED → DRAFT) — it
 * sends a card back with Return and a remark instead. Since 2026-10-15
 * ADMIN / SUPER_ADMIN may unsubmit, so the step is hidden only for non-admin
 * host logins; the API decides either way (and the record page follows `/actions`).
 */
export const hasHostRights = (me: MeResponse): boolean => isAdminRole(me.active_role) || groupGrants(me, "accreditation.approve");

/** The steps this login is never offered in the (bulk) status sheet. */
export const hiddenActionsFor = (me: MeResponse): ReadonlySet<AcCardTransitionAction> =>
	hasHostRights(me) && !isAdminRole(me.active_role) ? new Set<AcCardTransitionAction>(["unsubmit"]) : new Set<AcCardTransitionAction>();

/** "Register new person": admins, or a group with the register permission. */
export const canRegister = (me: MeResponse): boolean => isAdminRole(me.active_role) || groupGrants(me, "accreditation.register");

/**
 * The list's lock icon on Edit. The organisation edits only DRAFT and
 * RETURN; the host can still fix a record in any status. The server decides
 * for real when the edit is saved.
 */
export const looksEditable = (status: AcCardStatus, role: RoleCode | null | undefined): boolean =>
	isAdminRole(role) || (AC_CARD_EDITABLE_STATUSES as readonly AcCardStatus[]).includes(status);

/** Edges leaving `from` that one of `sides` may take, one per action. */
export function edgesFor(from: AcCardStatus, sides: ReadonlySet<AcCardTransitionActor>): AcCardTransition[] {
	const seen = new Set<AcCardTransitionAction>();
	return allowedTransitions(from).filter((t) => {
		if (!sides.has(t.by) || seen.has(t.action)) return false;
		seen.add(t.action);
		return true;
	});
}

/** The edge `action` takes from `from` for these sides, if any. */
export const edgeFor = (from: AcCardStatus, action: AcCardTransitionAction, sides: ReadonlySet<AcCardTransitionActor>): AcCardTransition | undefined =>
	edgesFor(from, sides).find((t) => t.action === action);

/** Initials for the photo placeholder: first letters of the first and last word. */
export function initials(name: string): string {
	const words = name.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return "?";
	const first = words[0]!.charAt(0);
	const last = words.length > 1 ? words[words.length - 1]!.charAt(0) : "";
	return (first + last).toLocaleUpperCase();
}

/** Whole years from a `YYYY-MM-DD` date of birth to today. */
export function ageFrom(dob: string | null | undefined, now = new Date()): number | null {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob ?? "");
	if (!m) return null;
	const y = Number(m[1]);
	const mo = Number(m[2]) - 1;
	const d = Number(m[3]);
	let age = now.getFullYear() - y;
	if (now.getMonth() < mo || (now.getMonth() === mo && now.getDate() < d)) age--;
	return age >= 0 && age < 130 ? age : null;
}

/**
 * A calendar date (`YYYY-MM-DD`: date of birth, passport dates) as
 * DD/MM/YYYY — "12/09/1990" (`formatYmd`). No time zone can move it a day.
 */
export function formatDateOnly(ymd: string | null | undefined): string | null {
	return /^(\d{4})-(\d{2})-(\d{2})/.test(ymd ?? "") ? formatYmd(ymd) : null;
}

/** Stored gender values are free text (e.g. MALE / FEMALE); map the known ones to a label key. */
export function genderKey(g: string | null | undefined): "MALE" | "FEMALE" | null {
	const v = (g ?? "").trim().toUpperCase();
	if (v === "MALE" || v === "M") return "MALE";
	if (v === "FEMALE" || v === "F") return "FEMALE";
	return null;
}

/** "+60 16972573". */
export const phoneOf = (cc: string | null | undefined, n: string | null | undefined): string | null => {
	if (!n) return null;
	const code = (cc ?? "").replace(/^\+/, "");
	return code ? `+${code} ${n}` : n;
};

// ── Journey ─────────────────────────────────────────────────────────────────
// The stepper is the API's (`journey`, contracts `cardJourney`, 0075): which steps a viewer
// gets and where the card stands are never derived here (`JourneyTracker` draws them).

/**
 * Which organisations this login can read, for the Organisation filter (B7).
 * `null` = no narrowing known (admins, or a scope row with any organisation):
 * offer every organisation. A list = the organisations its group's scope rows
 * name. The filter is shown unless that list is ONE organisation — there is
 * nothing to choose. Purely a UI choice: scope is enforced in SQL whatever
 * the URL says, and a URL value is always sent.
 */
export function readableOrganisations(me: MeResponse): { show: boolean; ids: string[] | null } {
	if (isAdminRole(me.active_role) || !roleHasGmsAccess(me.active_role, me.group) || !me.group) return { show: true, ids: null };
	const rows = me.group.scopes;
	if (rows.length === 0 || rows.some((r) => r.responsible_organization === null)) return { show: true, ids: null };
	const ids = [...new Set(rows.map((r) => r.responsible_organization!.id))];
	return { show: ids.length > 1, ids };
}
